import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Alert } from '@shared/protocol';
import { api } from './api';
import { socket } from './socket';

const MUTE_KEY = 'nkpa-alert-mute';

interface AlertState {
  toasts: Alert[];
  unacknowledged: number;
  dismiss: (id: number) => void;
  acknowledge: (ids: number[] | 'all') => Promise<void>;
  muted: boolean;
  setMuted: (m: boolean) => void;
  refreshCount: () => void;
}

const Ctx = createContext<AlertState | null>(null);

/** Two short tones; generated with Web Audio so no audio file is needed. */
function chime(severity: Alert['severity']): void {
  try {
    const ctx = new AudioContext();
    const tones = severity === 'FAIL' ? [880, 660, 880] : [660, 880];
    tones.forEach((f, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = f;
      const t = ctx.currentTime + i * 0.18;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.18, t + 0.02);
      gain.gain.linearRampToValueAtTime(0, t + 0.15);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.16);
    });
    setTimeout(() => void ctx.close(), 1000);
  } catch {
    // Audio blocked by the browser (no user interaction yet): the on-screen alert still shows.
  }
}

export function AlertProvider({ children, soundEnabled }: { children: ReactNode; soundEnabled: boolean }) {
  const [toasts, setToasts] = useState<Alert[]>([]);
  const [unacknowledged, setUnacknowledged] = useState(0);
  const [muted, setMutedState] = useState(() => {
    try {
      return localStorage.getItem(MUTE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const soundRef = useRef(soundEnabled && !muted);
  soundRef.current = soundEnabled && !muted;

  const refreshCount = useCallback(() => {
    api<{ alerts: Alert[] }>('GET', '/api/alerts?unacknowledged=true&limit=1000')
      .then((r) => setUnacknowledged(r.alerts.length))
      .catch(() => {});
  }, []);

  useEffect(() => {
    socket.start();
    refreshCount();
    return socket.onMessage((msg) => {
      if (msg.type !== 'alert') return;
      setToasts((t) => [msg.alert, ...t].slice(0, 5));
      setUnacknowledged((n) => n + 1);
      if (soundRef.current) chime(msg.alert.severity);
      if (msg.alert.severity === 'WARNING') {
        setTimeout(() => setToasts((t) => t.filter((x) => x.id !== msg.alert.id)), 20_000);
      }
    });
  }, [refreshCount]);

  const value = useMemo<AlertState>(
    () => ({
      toasts,
      unacknowledged,
      dismiss: (id) => setToasts((t) => t.filter((x) => x.id !== id)),
      acknowledge: async (ids) => {
        await api('POST', '/api/alerts/acknowledge', { ids });
        setToasts((t) => (ids === 'all' ? [] : t.filter((x) => !ids.includes(x.id))));
        refreshCount();
      },
      muted,
      setMuted: (m) => {
        setMutedState(m);
        try {
          localStorage.setItem(MUTE_KEY, m ? '1' : '0');
        } catch {
          // Per-browser convenience only.
        }
      },
      refreshCount,
    }),
    [toasts, unacknowledged, muted, refreshCount],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAlerts(): AlertState {
  const c = useContext(Ctx);
  if (!c) throw new Error('useAlerts outside AlertProvider');
  return c;
}
